import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useAsyncCallback } from 'react-async-hook';
import { useMutation } from '@apollo/client';
import { StreamChat } from 'stream-chat';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import getConfig from 'next/config';
import { DefaultStreamChatGenerics } from 'stream-chat-react/dist/types/types';
import objectHasProperty from '@tools/objectHasProperty';
import { GENERATE_CHAT_TOKEN } from '@graphql/operations/chat/generateChatToken';

const { publicRuntimeConfig } = getConfig();
const LOG = LoggerFactory.create('useStreamChat');

/**
 * Stream logs are important to capture in our system. However, some logs are incredibly
 * spammy sending every second. This creates a capture net to log the first instance
 * but prevent future logs sending to splunk
 *
 * @returns A function isSpammyWarnLog indicating if it is a spammy log message (true) or
 * if it should be logged
 */
export const createSpammyWarnChecker = () => {
  const spammyMessageTracker = {};
  const spammyMessages = ['connection:_errorFromWSEvent() - WS failed with code'];

  return message =>
    spammyMessages.some(spammyMessage => {
      if (!message.startsWith(spammyMessage)) {
        return false;
      }
      if (!spammyMessageTracker[spammyMessage]) {
        spammyMessageTracker[spammyMessage] = true;
        // Return false for the first message so it is still logged
        return false;
      }
      return spammyMessageTracker[spammyMessage];
    });
};

export type ChatUser = { firstName?: string; lastName?: string; attendeeId?: string };

export interface StreamType extends DefaultStreamChatGenerics {
  userType: ChatUser;
}

/**
 * Hook for managing the stream connection for attendee massaing.
 */
export const useStreamChat = () => {
  const [getStreamCredentials] = useMutation(GENERATE_CHAT_TOKEN);

  const { query: { streamEndpointOverride } = {} } = useRouter();

  const asyncConnect = useAsyncCallback(async () => {
    try {
      LOG.debug('Getting stream credentials');
      const { data } = await getStreamCredentials();
      const { apiKey, userId, token } = data?.generateChatToken || {};
      const endpoint =
        (publicRuntimeConfig.CUSTOMER_FACING !== 'true' && streamEndpointOverride) ||
        publicRuntimeConfig.CHAT_SERVICE_STREAM_ENDPOINT;
      LOG.debug(`Initializing stream using ${endpoint}...`);
      const isSpammyWarnLog = createSpammyWarnChecker();
      const client = StreamChat.getInstance<StreamType>(apiKey, {
        // https://github.com/GetStream/stream-chat-js/blob/master/docs/logging.md
        logger: (logLevel, message, extraData) => {
          const { url } = extraData;
          switch (logLevel) {
            case 'error': {
              const { error } = extraData;
              LOG.error(message, {
                url,
                errorMessage: objectHasProperty(error, 'message') ? error?.message : ''
              });
              break;
            }
            case 'warn':
              if (!isSpammyWarnLog(message)) {
                LOG.warn(message, { url });
              }
              break;
            default: {
              /**
               * Logging out all messages from Stream is spamming the browser console during local development.
               * Uncomment the code below if you need to log them out for debugging purpose.
               * */
              // const { error } = extraData;
              // LOG.debug(message, error?.message);
              break;
            }
          }
        },
        timeout: 10000,
        // We need to enable WSFallback flag to use the `longpoll` mechanism in case of Websocket is
        // blocked by proxy or any blockers.
        enableWSFallback: true
      });
      client.setBaseURL(endpoint);

      const tokenProvider = () => {
        let useExistingToken = true;
        return async () => {
          if (useExistingToken) {
            useExistingToken = false;
            return token;
          }
          const { data: mutationData } = await getStreamCredentials();
          return mutationData?.generateChatToken?.token;
        };
      };

      await client.connectUser({ id: userId }, tokenProvider());
      LOG.debug('Stream initialized');
      return client;
    } catch (error) {
      LOG.error('Could not connect to stream', error);
      // By throwing the exact error object, the consumer is able to get more error info.
      throw error;
    }
  });

  const getStreamClient = useCallback(
    async () => asyncConnect.currentPromise || asyncConnect.execute(),
    [asyncConnect]
  );

  return {
    getStreamClient,
    loading: asyncConnect.loading
  };
};
